/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  CellClassParams,
  ColDef,
  GridApi,
  GridReadyEvent,
  INumberCellEditorParams,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy,
} from 'ag-grid-community';
import {isNumeric} from 'rxjs/internal-compatibility';
import {DISMISS_MSG, EXCEL_GREEN, EXCEL_YELLOW} from '../../../../common/constants';
import {AutocompleteFilterComponent} from '../../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {CopyPaste} from '../../../../core/helpers/copy-paste';
import {range} from '../../../../core/helpers/generic-helpers';
import {StateService} from '../../../../core/state/state.service';
import {SelectableOptions} from '../../../../models/selectable-options';
import {VehicleScenarioService} from '../../../../services/vehicle-scenario.service';

@Component({
    selector: 'app-vehicle-forecast-quick-edit',
    templateUrl: './vehicle-forecast-quick-edit.component.html',
    styleUrl: './vehicle-forecast-quick-edit.component.scss',
    providers: [CopyPaste],
    standalone: false
})
export class VehicleForecastQuickEditComponent {
  protected gridApi!: GridApi;
  requestDTO: any;

  isUpdateEnabled: boolean = true;
  excludedHeaders = ["", "PSOS", "PEOS"];
  columnDefinition: ColDef[] = [];
  displayedColumns = new SelectableOptions();
  defaultDisplayedColumns = ["PLNGMKT", "BRANDFAM", "MKTDIV", "LBRAND", "ARCH", "PRGMCODE", "BODYSTYLE", "TOS",
    "PROP_SEG", "RECID", "SOS", "EOS", "PORTSTAT", "COMMENTS"];
  columns: string[] = [];
  tableData: any[] = [];
  showSpinner: boolean;
  pageSize: number;

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };

  columnTypes = {
    editableColumn: {
      editable: true,
      singleClickEdit: false,
      cellStyle: (params: CellClassParams) => {
        if (this.copyPaste.isCellSelected(params, this.columnDefinition)) {
          return {backgroundColor: "#2244CC44"};
        } else {
          switch (this.getShadedYearColor(params)) {
            case 1:
              return {backgroundColor: EXCEL_GREEN}
            case 2:
              return {backgroundColor: EXCEL_YELLOW}
            default:
              return {backgroundColor: "#ffffff"}
          }
        }
      },
    },
    commentColumn: {
      editable: true,
      singleClickEdit: false,
      cellStyle: (params: CellClassParams) => {
        if (this.copyPaste.isCellSelected(params, this.columnDefinition)) {
          return {backgroundColor: "#2244CC44"};
        } else {
          return {backgroundColor: EXCEL_YELLOW}
        }
      },
    }
  };


  constructor(@Inject(MAT_DIALOG_DATA) private readonly data: any,
              public dialogRef: MatDialogRef<VehicleForecastQuickEditComponent>,
              readonly vehicleScenarioService: VehicleScenarioService,
              public snackBar: FabMessageService,
              readonly stateService: StateService,
              public copyPaste: CopyPaste) {
    this.pageSize = 100;
    if (data) {
      this.requestDTO = data.requestDTO;
    }
  }

  /**
   * Gets the grid API and data when the grid is ready
   * @param params the parameters generated by the grid event
   */
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.getForecastData();
  }

  /**
   * Gets the forecast template data and sets the table
   */
  getForecastData() {
    this.showSpinner = true;
    this.vehicleScenarioService.getForecastTemplateData(this.requestDTO).subscribe({
      next: (response: any[]) => {
        this.tableData = response;
      },
      error: (error) => {
        this.snackBar.displayError(error, DISMISS_MSG);
        this.showSpinner = false;
      },
      complete: () => {
        this.setTable();
        this.showSpinner = false;
      }
    });
  }

  /**
   * Sets the table column definitions and autosizes the columns
   */
  setTable() {
    if (this.tableData.length > 0) {
      const startYear = this.requestDTO.startYear;
      const endYear = this.requestDTO.endYear;
      const years = range(startYear, endYear + 1).map((year) => year.toString());
      const yearColumns: ColDef[] = [];
      const stringColumns: ColDef[] = [];
      const row = this.tableData[0];
      const keys = Object.keys(row);
      const headers = keys.filter((key) => !this.excludedHeaders.includes(key));
      for (const header of headers) {
        if (years.includes(header)) {
          yearColumns.push({
            field: header,
            headerName: header,
            type: "editableColumn",
            cellEditor: "agNumberCellEditor",
            cellEditorParams: {
              min: 0,
            } as INumberCellEditorParams,
          });
        } else {
          const isComment = header.toLowerCase() === 'comments'.toLowerCase();
          this.displayedColumns.options.push(header);
          stringColumns.push({
            field: header,
            headerName: header,
            type: isComment ? "commentColumn" : "",
            filter: AutocompleteFilterComponent,
          });
        }
      }
      this.displayedColumns.selectedOptions = this.defaultDisplayedColumns;
      this.columnDefinition = stringColumns.concat(yearColumns);
      this.onColumnUpdate();
      this.gridApi.autoSizeAllColumns(true);
    }
  }

  /**
   * updates the displayed columns with the selections from the dropdown
   */
  onColumnUpdate() {
    this.gridApi.setGridOption("columnDefs", this.columnDefinition.filter(column =>
      this.displayedColumns.selectedOptions.includes(column.field) || isNumeric(column.field)));
  }

  /**
   * Updates every record in the forecast data table with the currently entered data, even if there are no changes
   */
  updateForecast() {
    this.showSpinner = true;
    const updateData = [];
    this.gridApi.forEachNodeAfterFilter((rowNode) => {
      updateData.push(rowNode.data);
    });
    this.vehicleScenarioService.updateVehicleForecast(updateData, this.stateService.getUserGmid()).subscribe({
      error: err => {
        this.snackBar.displayError(err, DISMISS_MSG);
        this.showSpinner = false;
      },
      complete: () => {
        this.showSpinner = false;
        this.snackBar.displaySuccess("Successfully updated scenario");
        this.onCancel();
      }
    });
  }

  /**
   * Closes the pop up
   */
  onCancel() {
    this.dialogRef.close();
  }

  getShadedYearColor(params: CellClassParams): number {
    const row = params.data;
    const startOfsale = this.requestDTO.shadeByPortfolio ? row.PSOS : row.SOS;
    const endOfsale = this.requestDTO.shadeByPortfolio ? row.PEOS : row.EOS;

    // Skip the row if the PSOS or EOS col value is 0
    if (startOfsale === 0 || endOfsale === 0) {
      return 0;
    }
    const sos = new Date(startOfsale);
    const eos = new Date(endOfsale);
    const year = Number(params.column.getColId());

    if (year < sos.getFullYear() || year > eos.getFullYear()) {
      return 0;
    }

    // Highlight the cal year cell green if it is a partial year, otherwise highlight it yellow
    const isPartialStartYear = (year === sos.getFullYear()
      && sos > (new Date("01/31/" + sos.getFullYear())));
    const isPartialEndYear = year === eos.getFullYear()
      && eos < (new Date("12/01/" + eos.getFullYear()));
    if (isPartialStartYear || isPartialEndYear) {
      return 1
    } else {
      return 2
    }
  }
}
